import type { UserRoleType } from "@/contexts/AuthContext";
import type { NurseryRole } from "@/contexts/NurseryContext";

/** Human-readable labels for nursery job-title roles. */
export const NURSERY_ROLE_LABELS: Record<string, string> = {
  MANAGER: "Manager",
  DEPUTY_MANAGER: "Deputy Manager",
  LEADER: "Leader",
  PRACTITIONER: "Practitioner",
};

/** What each nursery role can do, shown when choosing one. */
export const NURSERY_ROLE_DESCRIPTIONS: Record<string, string> = {
  MANAGER:
    "Adds staff, edits their profiles and manages their logins. Only a group admin can change another manager.",
  DEPUTY_MANAGER:
    "Sees every staff profile, including health details, and reviews events. Cannot edit profiles or manage logins.",
  LEADER:
    "Completes checklists and tasks, reports events, and reads and signs documents sent to them.",
  PRACTITIONER:
    "Completes checklists and assigned tasks, and reads and signs documents sent to them.",
};

/** Labels for the org/platform access tiers that have no nursery job title. */
const ACCESS_TIER_LABELS: Record<string, string> = {
  PLATFORM_ADMIN: "Platform Admin",
  ORG_ADMIN: "Group Administrator",
  ORG_MEMBER: "Member",
};

/** Returns the display label for a nursery role, or "" when there is none. */
export function nurseryRoleLabel(role: NurseryRole | null | undefined): string {
  if (!role) return "";
  return NURSERY_ROLE_LABELS[role] ?? "";
}

/** Display label for the user's role across all tiers; NURSERY_* reuse the nursery job titles. */
export function userRoleTypeLabel(
  role: UserRoleType | null | undefined,
): string {
  if (!role || role === "UNKNOWN") return "";
  return (
    ACCESS_TIER_LABELS[role] ??
    nurseryRoleLabel(role.replace(/^NURSERY_/, "") as NurseryRole)
  );
}

/** Show the nursery switcher only when the user can access more than one nursery. */
export function shouldShowNurserySwitcher(nurseryCount: number): boolean {
  return nurseryCount > 1;
}

/**
 * Who may report an event (e.g. a fire drill) from the Workspace's Events tab.
 * Platform admins and group (organisation) admins always can, even though they
 * hold no per-nursery role. Nursery staff can unless they are a practitioner.
 * Mirrors the EventRecord create policy enforced server-side.
 */
export function canReportEvents(args: {
  isPlatformAdmin: boolean;
  isOrgAdmin: boolean;
  nurseryRole: NurseryRole | null;
}): boolean {
  const { isPlatformAdmin, isOrgAdmin, nurseryRole } = args;
  return (
    isPlatformAdmin ||
    isOrgAdmin ||
    (nurseryRole !== null && nurseryRole !== "PRACTITIONER")
  );
}

// `canManageEventLifecycle` and `canDeleteOrReopenEventRecords` lived here until
// NAU-342. They mirrored server rules that are now per-nursery configuration, so
// any client copy of them is a guess: the API returns the resolved capability on
// each record instead (EventRecordCapabilitiesDto), and the Events Hub and record
// detail page read that.

/**
 * Who may create a task from the Task Manager ("managers and above").
 *
 * The Task Manager page itself is open to every role — ZenStack narrows the rows
 * each user sees — so this gates only the "+ Add Task" action. Deputy managers
 * are excluded, matching the sidebar's manager set and the fire-extinguisher
 * gate rather than the broader `canAcknowledgeEvents`.
 */
export function canManageTasks(args: {
  isPlatformAdmin: boolean;
  isOrgAdmin: boolean;
  nurseryRole: NurseryRole | null;
}): boolean {
  const { isPlatformAdmin, isOrgAdmin, nurseryRole } = args;
  return isPlatformAdmin || isOrgAdmin || nurseryRole === "MANAGER";
}

/**
 * Who may raise a follow-up action against a failed check. Mirrors the
 * FollowUpAction create policy (platform admin, org admin, nursery MANAGER; not
 * deputies). Like {@link canManageTasks} but reads the org tier from `userRoleType`.
 */
export function canRaiseFollowUp(args: {
  isPlatformAdmin: boolean;
  userRoleType: UserRoleType | null | undefined;
  nurseryRole: NurseryRole | null;
}): boolean {
  const { isPlatformAdmin, userRoleType, nurseryRole } = args;
  return (
    isPlatformAdmin || userRoleType === "ORG_ADMIN" || nurseryRole === "MANAGER"
  );
}

/** Who may sign off, or return to open, a follow-up action waiting for approval. */
export function canSignOffFollowUp(args: {
  userRoleType: UserRoleType | null | undefined;
}): boolean {
  const { userRoleType } = args;
  return (
    userRoleType === "NURSERY_MANAGER" ||
    userRoleType === "ORG_ADMIN" ||
    userRoleType === "PLATFORM_ADMIN"
  );
}

/**
 * Who may acknowledge (review) a submitted event record. Platform and group
 * (organisation) admins always can; among nursery staff only managers and
 * deputy managers can. Mirrors the EventRecordAcknowledgement create policy
 * enforced server-side.
 */
export function canAcknowledgeEvents(args: {
  isPlatformAdmin: boolean;
  isOrgAdmin: boolean;
  nurseryRole: NurseryRole | null;
}): boolean {
  const { isPlatformAdmin, isOrgAdmin, nurseryRole } = args;
  return (
    isPlatformAdmin ||
    isOrgAdmin ||
    nurseryRole === "MANAGER" ||
    nurseryRole === "DEPUTY_MANAGER"
  );
}

/**
 * Who may acknowledge a checklist's failed checks (NAU-71). Narrower than
 * {@link canAcknowledgeEvents}: deputy managers are excluded, mirroring the
 * server-side AcknowledgementAuthorizationService.
 */
export function canAcknowledgeFailedChecks(args: {
  isPlatformAdmin: boolean;
  isOrgAdmin: boolean;
  nurseryRole: NurseryRole | null;
}): boolean {
  const { isPlatformAdmin, isOrgAdmin, nurseryRole } = args;
  return isPlatformAdmin || isOrgAdmin || nurseryRole === "MANAGER";
}

/**
 * Who sees every member's fire extinguisher and emergency light checks rather
 * than only their own (NAU-411). Mirrors the server-side read policy on those
 * checks.
 */
export function canViewAllChecks(args: {
  isPlatformAdmin: boolean;
  isOrgAdmin: boolean;
  nurseryRole: NurseryRole | null;
}): boolean {
  const { isPlatformAdmin, isOrgAdmin, nurseryRole } = args;
  return (
    isPlatformAdmin ||
    isOrgAdmin ||
    nurseryRole === "MANAGER" ||
    nurseryRole === "DEPUTY_MANAGER"
  );
}
